import React, { useState } from "react";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useGlobalState } from "../contexts/GlobalContext";
import { apiFetch } from "../utils/api";
import { getStripeCheckoutSessionStatus } from "../utils/stripeApi";
import "../Stylesheets/Login.css";

function getBillingParams(location) {
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams((location.hash || "").replace(/^#/, ""));

  return {
    billingState: searchParams.get("billing") || hashParams.get("billing"),
    emailFromQuery: searchParams.get("email") || hashParams.get("email"),
    sessionId: searchParams.get("session_id") || hashParams.get("session_id"),
  };
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const { dispatch } = useGlobalState();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const { billingState, emailFromQuery, sessionId } = getBillingParams(location);
    const stateInfo = location.state?.info;

    if (emailFromQuery && !email) {
      setEmail(emailFromQuery);
    }

    if (billingState === "success" && sessionId) {
      getStripeCheckoutSessionStatus(sessionId)
        .then((session) => {
          if (session.status === "complete" && ["paid", "unpaid", "no_payment_required"].includes(session.payment_status)) {
            setInfo("Subscription checkout completed. Verify your email if needed, then sign in to access your workspace.");
            setError(null);
            return;
          }

          setError("Stripe checkout did not complete successfully. You can sign in and retry from Account.");
          setInfo(null);
        })
        .catch((err) => {
          setError(err.message || "Unable to verify Stripe checkout status.");
          setInfo(null);
        });
    } else if (billingState === "success") {
      setInfo("Subscription checkout completed. Verify your email if needed, then sign in to access your workspace.");
      setError(null);
    } else if (billingState === "cancelled") {
      setError("Stripe checkout was cancelled. You can sign in later and start the trial from Account.");
      setInfo(null);
    } else if (stateInfo) {
      setInfo(stateInfo);
    }
  }, [location.search, location.hash, location.state, email]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setNeedsVerification(false);

    try {
      const result = await apiFetch("login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });

      const brokerage = result.user?.brokerage?.id
        ? {
            id: result.user.brokerage.id,
            office_id: result.user.brokerage.officeId,
            office_business_name: result.user.brokerage.officeBusinessName,
            broker: result.user.brokerage.designatedRealtor,
            office_city: result.user.brokerage.officeStreetCity,
            office_state: result.user.brokerage.officeStreetState,
            office_street_country:
              result.user.brokerage.officeStreetCountry || "US",
            phone_number: result.user.brokerage.phoneNumber,
          }
        : null;
      const team = result.user?.team
        ? {
            id: result.user.team.id,
            public_id: result.user.team.publicId,
            name: result.user.team.name,
            team_type: result.user.team.teamType,
            email: result.user.team.email,
            phone: result.user.team.phone,
            website: result.user.team.website,
            street_address: result.user.team.streetAddress,
            city: result.user.team.city,
            state: result.user.team.state,
            postal_code: result.user.team.postalCode,
            country: result.user.team.country,
            description: result.user.team.description,
            member_role: result.user.team.memberRole,
          }
        : null;

      const userPayload = {
        ...result.user,
        brokerage,
        team,
        token: result.token,
      };

      dispatch({ type: "SET_USER", payload: userPayload });
      localStorage.setItem("token", result.token);
      navigate("/home");
    } catch (err) {
      setError(err.message || "Login failed");
      if (err?.message?.toLowerCase().includes("verify your email")) {
        setNeedsVerification(true);
      }
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError("Enter your email above so we can resend the verification link.");
      return;
    }

    setResending(true);
    setInfo(null);
    setError(null);
    try {
      const res = await apiFetch("resendVerification", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setInfo(res.message || "Verification email sent.");
    } catch (err) {
      setError(err.message || "Unable to resend verification email.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="crm-login">
      <section className="crm-login__brand-panel">
        <div className="crm-login__brand-backdrop" />
        <div className="crm-login__brand-content">
          <span className="crm-login__eyebrow">Signl CRM</span>
          <img
            src="/assets/whiteSgnl.png"
            alt="Signl CRM"
            className="crm-login__brand-logo"
          />
          <h1>Low-cost CRM built for teams that need clarity, not clutter.</h1>
          <p>
            Manage contacts, keep follow-ups moving, and maintain a clean
            pipeline on a lean CRM that is ready to connect with your own AI agents and bots.
          </p>
          <ul className="crm-login__brand-list">
            <li>Affordable seats at $7 per user each month</li>
            <li>Use your own AI stack instead of paying for bundled CRM AI</li>
            <li>Fast access to contacts, reminders, and workflow visibility</li>
          </ul>
        </div>
      </section>

      <section className="crm-login__form-panel">
        <div className="crm-login__form-shell">
          <div className="crm-login__form-brand">
            <img
              src="/assets/blackSgnl.png"
              alt="Signl CRM"
              className="crm-login__form-logo"
            />
            <Link to="/" className="crm-login__back-link">
              Back to home
            </Link>
          </div>

          <div className="crm-login__card">
            <span className="crm-login__eyebrow crm-login__eyebrow--dark">
              Welcome back
            </span>
            <h2>Sign in to your workspace</h2>
            <p>
              Access your CRM dashboard and pick up where your team left off.
            </p>

            <form onSubmit={handleLogin} className="crm-login__form">
              <label className="crm-login__field">
                <span>Email</span>
                <div className="crm-login__input-shell">
                  <FaUser className="crm-login__input-icon" />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </label>

              <label className="crm-login__field">
                <span>Password</span>
                <div className="crm-login__input-shell">
                  <FaLock className="crm-login__input-icon" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="crm-login__toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </label>

              <div className="crm-login__meta">
                <label className="crm-login__remember">
                  <input type="checkbox" />
                  <span>Remember me</span>
                </label>
              </div>

              <button type="submit" className="crm-login__submit">
                Sign in
              </button>

              {error && (
                <div className="crm-login__message crm-login__message--error">
                  <span>{error}</span>
                  {needsVerification && (
                    <button
                      type="button"
                      className="crm-login__message-action"
                      onClick={handleResendVerification}
                      disabled={resending}
                    >
                      {resending ? "Sending..." : "Resend verification email"}
                    </button>
                  )}
                </div>
              )}

              {info && (
                <div className="crm-login__message crm-login__message--info">
                  <span>{info}</span>
                </div>
              )}
            </form>

            <p className="crm-login__signup-copy">
              Need an account? <Link to="/signUp">Create one</Link>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Login;
